import React from 'react';
import { useSpc } from '../../context/SpcContext';
import { MapPin, Plus, Trash2 } from 'lucide-react';

function formatParkrunDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

export default function Parkrun() {
  const { state, role, user, attendParkrun, cancelParkrun, deleteParkrunLocation } = useSpc();
  const pr = state.parkrun || { date: null, myLocation: null, locations: [], groups: [] };
  const [picked, setPicked] = React.useState('');
  const [customLoc, setCustomLoc] = React.useState('');
  const [adding, setAdding] = React.useState(false);

  const totalGoing = pr.groups.reduce((n, g) => n + g.attendees.length, 0);

  const submit = async (e) => {
    e.preventDefault();
    const loc = adding ? customLoc.trim() : picked;
    if (!loc) return;
    await attendParkrun(loc);
    setCustomLoc(''); setPicked(''); setAdding(false);
  };

  return (
    <>
      <div className="section-head">
        <div>
          <div className="eyebrow">Every Saturday</div>
          <h2>Parkrun</h2>
          <p>{formatParkrunDate(pr.date)} · see where SPC members are running this week.</p>
        </div>
      </div>

      <div className="card span-12 accent" data-testid="parkrun-my-status" style={{ marginBottom: 20 }}>
        {pr.myLocation ? (
          <>
            <span className="pill green" style={{ alignSelf: 'flex-start' }}>YOU'RE IN</span>
            <h3 style={{ marginTop: 12 }}>Doing parkrun at {pr.myLocation}</h3>
            <p>See you there — or let us know if your plans change.</p>
            <div className="hero-actions" style={{ marginTop: 16 }}>
              <button className="btn secondary" onClick={() => cancelParkrun()} data-testid="parkrun-cancel-btn">I'm not going</button>
            </div>
          </>
        ) : (
          <>
            <span className="pill yellow" style={{ alignSelf: 'flex-start' }}>SAY YOU'RE GOING</span>
            <h3 style={{ marginTop: 12 }}>Which parkrun are you doing?</h3>
            <form onSubmit={submit} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14, alignItems: 'center' }}>
              {!adding ? (
                <>
                  <select value={picked} onChange={(e) => setPicked(e.target.value)} data-testid="parkrun-location-select" style={{ minWidth: 220 }}>
                    <option value="">Choose a parkrun…</option>
                    {pr.locations.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
                  </select>
                  <button type="submit" className="btn" disabled={!picked} data-testid="parkrun-attend-btn">Count me in</button>
                  <button type="button" className="btn ghost" onClick={() => setAdding(true)} data-testid="parkrun-add-new-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Plus size={14} /> Not listed? Add it</button>
                </>
              ) : (
                <>
                  <input type="text" placeholder="e.g. Bute Park parkrun" value={customLoc} maxLength={80} onChange={(e) => setCustomLoc(e.target.value)} data-testid="parkrun-new-location-input" style={{ minWidth: 220 }} />
                  <button type="submit" className="btn" disabled={!customLoc.trim()} data-testid="parkrun-attend-new-btn">Count me in</button>
                  <button type="button" className="btn ghost" onClick={() => setAdding(false)} data-testid="parkrun-cancel-add-btn">Cancel</button>
                </>
              )}
            </form>
          </>
        )}
      </div>

      <div className="section-head">
        <div><div className="eyebrow">{totalGoing} going</div><h2>Who's running where</h2><p>Grouped by parkrun — every new location you add grows this list for everyone.</p></div>
      </div>

      {!pr.groups.length ? (
        <div className="empty" data-testid="parkrun-empty">No one's signed up yet — be the first!</div>
      ) : (
        <div className="grid">
          {pr.groups.map(g => (
            <div className="card span-4" key={g.location} data-testid={`parkrun-group-${g.location}`}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={16} /> {g.location}</h3>
                <span className="pill">{g.attendees.length}</span>
              </div>
              <ul style={{ marginTop: 12, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {g.attendees.map(a => (
                  <li key={a.id} data-testid={`parkrun-attendee-${a.id}`}>{a.name}{a.id === user?.id ? ' (you)' : ''}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {role === 'admin' && pr.locations.length > 0 && (
        <>
          <div className="section-head"><div><div className="eyebrow">Admin</div><h2>Manage the parkrun list</h2><p>Remove a location if it's a typo or duplicate — past RSVPs keep showing the original name.</p></div></div>
          <div className="card span-12" data-testid="parkrun-admin-locations">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {pr.locations.map(l => (
                <span key={l.id} className="pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {l.name}
                  <button type="button" onClick={() => { if (window.confirm(`Remove ${l.name} from the parkrun list?`)) deleteParkrunLocation(l.id); }} data-testid={`parkrun-remove-location-${l.id}`} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'inline-flex', padding: 4 }} title={`Remove ${l.name}`}>
                    <Trash2 size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
